import { useState } from "react";
import { useParams } from "react-router-dom";

import { BookingConflictsSection } from "~/modals/Providers/ProviderDetailsModal/components/BookingConflictsSection";
import { MODAL_ROUTES, useNavigateModal } from "~/router";
import { DeleteScheduleConfirmation } from "~/screens/Admins/components/DeleteScheduleConfirmation";
import { StatusBadge } from "~/screens/Admins/components/StatusBadge";
import { usePermissions } from "~/screens/Admins/hooks/usePermissions";
import { adminProviders } from "~/services";
import { useGetProviderOrphanedAppointments } from "~/services/admin-providers";
import { downloadBlob } from "~/shared/utils/download-blob";
import { errorToast, Modal } from "~/ui";
import { AppointmentListSection, ProviderScheduleSection } from "./components";

export const ProviderDetailsModal = ({
  show,
  onClose,
}: {
  show: boolean;
  onClose: () => void;
}) => {
  const { providerId } = useParams<{ providerId: string }>();
  const navigateModal = useNavigateModal();
  const exportSchedule = adminProviders.useExportProviderSchedule();
  const downloadScheduleChangeLog =
    adminProviders.useDownloadScheduleChangeLog();
  const { canEditProviderSchedule } = usePermissions();

  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { data: scheduleMetaResponse, isLoading: isLoadingProviderSchedule } =
    adminProviders.useGetProviderScheduleMeta(providerId ?? "");

  const {
    data: orphanedAppointmentsResponse,
    isFetching: isFetchingOrphanedAppointments,
    refetch: refetchConflicts,
  } = useGetProviderOrphanedAppointments(providerId ?? "", show);

  const deleteScheduleMutation =
    adminProviders.useDeleteProviderScheduleMutation(providerId ?? "", {
      onCloseModal: () => setShowDeleteModal(false),
    });

  if (!providerId || !show || isLoadingProviderSchedule) return null;

  const schedule = scheduleMetaResponse?.data ?? null;
  const scheduleData = schedule?.scheduleData ?? null;
  const isAvailable = !!scheduleData;
  const conflictCount = orphanedAppointmentsResponse?.data?.length ?? 0;
  const hasBookingConflicts = conflictCount > 0;

  const handleEdit = () => {
    navigateModal(`${MODAL_ROUTES.editProviderSchedule}/${providerId}`);
  };

  const handleExport = async () => {
    try {
      if (!scheduleData) return;

      const response = await exportSchedule(providerId);
      downloadBlob(
        new Blob([response.data]),
        scheduleData.originalFilename ?? "schedule.csv",
      );
    } catch (error) {
      errorToast(error);
    }
  };

  const handleDownloadLog = async () => {
    try {
      const response = await downloadScheduleChangeLog(providerId);
      downloadBlob(new Blob([response.data]), "schedule-change-log.csv");
    } catch (error) {
      errorToast(error);
    }
  };

  const handleDeleteConfirm = async () => {
    await deleteScheduleMutation.mutateAsync(scheduleData?.originalFilename);
    setShowDeleteModal(false);
  };

  return (
    <Modal
      className="gap-6"
      show={show}
      title="Clinician Details"
      onClose={onClose}
      headerCloseIcon
    >
      <div className="flex flex-col gap-6">
        <header className="flex w-full items-center justify-between">
          <h2 className="text-indigo text-xl font-medium">
            {schedule?.providerName}
          </h2>
          <StatusBadge isAvailable={isAvailable} />
        </header>
        <div className="border-light-lavender-gray flex w-full items-center justify-between gap-4 border-t py-4">
          <ProviderScheduleSection
            scheduleAvailable={isAvailable}
            fileName={scheduleData?.originalFilename}
            lastModified={scheduleData?.updatedAt}
            canEdit={canEditProviderSchedule}
            canDownloadLog={canEditProviderSchedule}
            onEdit={handleEdit}
            onExport={handleExport}
            onDelete={() => setShowDeleteModal(true)}
            onDownloadLog={handleDownloadLog}
          />
        </div>
        {scheduleData && <AppointmentListSection providerId={providerId} />}
        {hasBookingConflicts && (
          <BookingConflictsSection
            appointments={orphanedAppointmentsResponse?.data ?? []}
            totalConflicts={conflictCount}
            onRefresh={refetchConflicts}
            isFetching={isFetchingOrphanedAppointments}
          />
        )}
      </div>

      <DeleteScheduleConfirmation
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
      />
    </Modal>
  );
};
